import React from 'react';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
  QueryCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Error handler for authentication and network issues
const handleQueryError = (error: any) => {
  if (error?.status === 401) {
    queryClient.clear();
    window.location.href = '/login';
    return;
  }

  if (error?.status === 403) {
    console.error('Access denied:', error.message);
    // Could show a toast notification here
    return;
  }

  if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
    console.warn('Network error detected:', error.message);
    return;
  }

  console.error('Query error:', error);
};

// Exponential backoff, capped at 30s
const retryDelay = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

const queryCache = new QueryCache({
  onError: (error, query) => {
    console.error(
      `Query failed for key: ${JSON.stringify(query.queryKey)}`,
      error
    );
    handleQueryError(error);
  },
});

const mutationCache = new MutationCache({
  onError: (error) => {
    console.error(`Mutation failed:`, error);
    handleQueryError(error);
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        if (
          error?.status >= 400 &&
          error?.status < 500 &&
          ![408, 429].includes(error.status)
        ) {
          handleQueryError(error);
          return false;
        }
        return failureCount < 3;
      },
      retryDelay,
      // Off by default: every focus refetch counts against the admin-service
      // rate limit. The real-time hooks opt in per query (SPRITE-1468).
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
      networkMode: 'online',
      refetchInterval: false, // Will be set per query as needed
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.status === 403) {
          handleQueryError(error);
          return false;
        }
        // Don't retry client errors (4xx) except timeout and rate limit
        if (
          error?.status >= 400 &&
          error?.status < 500 &&
          ![408, 429].includes(error.status)
        ) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay,
      networkMode: 'online',
      onError: handleQueryError,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

// Utility functions for query management
export const invalidateAllQueries = () => {
  queryClient.invalidateQueries();
};

export const clearAllQueries = () => {
  queryClient.clear();
};

export const prefetchQuery = (queryKey: any[], queryFn: () => Promise<any>) => {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const setQueryData = (queryKey: any[], data: any) => {
  queryClient.setQueryData(queryKey, data);
};

export const getQueryData = (queryKey: any[]) => {
  return queryClient.getQueryData(queryKey);
};

export { queryClient };
